import { InlineMarkupType, MessageFormatType } from 'src/generated/graphql';
import { StringInfo, StringVariant } from '../types';
import { inlineMarkupLabel, messageFormatLabel } from 'src/common/formatLabels';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

export type ChangeType = 'added' | 'removed' | 'changed';

/** Placeholder rendered when a field has no value on one side of the diff. */
export const NO_VALUE = '—';

/**
 * A single field-level change within a string's diff. One row in the diff
 * table: the field's label plus its before/after values.
 */
export interface FieldDiff {
  /** Human-readable field label, e.g. "Source text", "Context note". */
  field: string;
  /** Value before the change; {@link NO_VALUE} when absent on this side. */
  previous: string;
  /** Value after the change; {@link NO_VALUE} when absent on this side. */
  current: string;
}

/**
 * The group-level CX ID membership change between two versions. CX IDs are
 * string-group metadata (which storefronts the group targets), not a per-string
 * field, so they are diffed separately from {@link DiffResult} and rendered in
 * their own section rather than as table rows keyed by stringId.
 */
export interface CxIdDiff {
  /** CX IDs present in the new version but not the old one. */
  added: string[];
  /** CX IDs present in the old version but not the new one. */
  removed: string[];
}

export interface DiffResult {
  stringId: string;
  changeType: ChangeType;
  /**
   * The per-field breakdown driving the diff table. For 'added'/'removed' this
   * is a single row describing the source text; for 'changed' it lists every
   * field that actually differs — source text, context note, inline markup,
   * message format, and CX variants — so metadata-only edits (a context note or
   * markup change with unchanged source text) are visible instead of rendering
   * as a "changed" row whose two text columns look identical.
   */
  fields: FieldDiff[];
}

/** Renders a text/optional value for display, falling back to the placeholder. */
function displayText(value: string | undefined | null): string {
  return value ? value : NO_VALUE;
}

function displayMarkup(
  t: TranslateFn,
  value: InlineMarkupType | undefined,
): string {
  return value ? inlineMarkupLabel(t, value) : NO_VALUE;
}

function displayFormat(
  t: TranslateFn,
  value: MessageFormatType | undefined,
): string {
  return value ? messageFormatLabel(t, value) : NO_VALUE;
}

/**
 * Computes the diff between two versions of a string group's strings.
 * Comparison uses stringId as the stable key and considers all source fields
 * and CX variants. A string is 'changed' exactly when it produces at least one
 * field-level difference.
 */
export function computeVersionDiff(
  t: TranslateFn,
  oldStrings: StringInfo[],
  newStrings: StringInfo[],
): DiffResult[] {
  const oldMap = new Map(oldStrings.map((s) => [s.stringId, s]));
  const newMap = new Map(newStrings.map((s) => [s.stringId, s]));
  const results: DiffResult[] = [];

  // Added or changed strings (present in new)
  for (const [stringId, newString] of newMap) {
    const oldString = oldMap.get(stringId);
    if (!oldString) {
      results.push({
        stringId,
        changeType: 'added',
        fields: [
          {
            field: NO_VALUE,
            previous: NO_VALUE,
            current: newString.sourceText,
          },
        ],
      });
    } else {
      const fields = computeFieldDiffs(t, oldString, newString);
      if (fields.length > 0) {
        results.push({ stringId, changeType: 'changed', fields });
      }
    }
  }

  // Removed strings (present in old but not new)
  for (const [stringId, oldString] of oldMap) {
    if (!newMap.has(stringId)) {
      results.push({
        stringId,
        changeType: 'removed',
        fields: [
          {
            field: NO_VALUE,
            previous: oldString.sourceText,
            current: NO_VALUE,
          },
        ],
      });
    }
  }

  return results;
}

/**
 * Builds one {@link FieldDiff} per source field that differs between the two
 * versions of a string. Optional fields normalize null/undefined/empty-string
 * to equal (matching the submit and rollback comparisons) so a no-op edit does
 * not surface as a change.
 */
function computeFieldDiffs(
  t: TranslateFn,
  oldString: StringInfo,
  newString: StringInfo,
): FieldDiff[] {
  const fields: FieldDiff[] = [];

  if (oldString.sourceText !== newString.sourceText) {
    fields.push({
      field: t('string-field-source-text'),
      previous: displayText(oldString.sourceText),
      current: displayText(newString.sourceText),
    });
  }
  if (oldString.sourceLocale !== newString.sourceLocale) {
    fields.push({
      field: t('string-field-source-locale'),
      previous: displayText(oldString.sourceLocale),
      current: displayText(newString.sourceLocale),
    });
  }
  if ((oldString.contextNote ?? '') !== (newString.contextNote ?? '')) {
    fields.push({
      field: t('string-field-context-note'),
      previous: displayText(oldString.contextNote),
      current: displayText(newString.contextNote),
    });
  }
  if (
    (oldString.inlineMarkupType ?? '') !== (newString.inlineMarkupType ?? '')
  ) {
    fields.push({
      field: t('string-field-inline-markup'),
      previous: displayMarkup(t, oldString.inlineMarkupType),
      current: displayMarkup(t, newString.inlineMarkupType),
    });
  }
  if (
    (oldString.messageFormatType ?? '') !== (newString.messageFormatType ?? '')
  ) {
    fields.push({
      field: t('string-field-message-format'),
      previous: displayFormat(t, oldString.messageFormatType),
      current: displayFormat(t, newString.messageFormatType),
    });
  }
  fields.push(
    ...computeVariantDiffs(t, oldString.variants, newString.variants),
  );

  return fields;
}

/**
 * Computes the group-level CX ID membership change between two versions. A CX ID
 * edit (adding or removing a targeted storefront) bumps the group version
 * without necessarily touching any string, so without this diff a version whose
 * only change is its CX IDs would render as "no differences" in the diff modal.
 * Results are de-duplicated and sorted so the rendered badges are deterministic
 * regardless of the backend's ordering.
 */
export function computeCxIdDiff(
  oldCxIds: string[],
  newCxIds: string[],
): CxIdDiff {
  const oldSet = new Set(oldCxIds);
  const newSet = new Set(newCxIds);
  const sortAlpha = (a: string, b: string) => a.localeCompare(b);
  return {
    added: [...new Set(newCxIds)]
      .filter((cxId) => !oldSet.has(cxId))
      .sort(sortAlpha),
    removed: [...new Set(oldCxIds)]
      .filter((cxId) => !newSet.has(cxId))
      .sort(sortAlpha),
  };
}

/**
 * Diffs CX variant overrides by cxId. A variant present on only one side is an
 * add/remove; one present on both contributes a row per differing property
 * (source text, context note). cxIds are visited in a stable sorted order so
 * the rows are deterministic regardless of the backend's variant ordering.
 */
function computeVariantDiffs(
  t: TranslateFn,
  oldVariants: StringVariant[],
  newVariants: StringVariant[],
): FieldDiff[] {
  const fields: FieldDiff[] = [];
  const oldByCx = new Map(oldVariants.map((v) => [v.cxId, v]));
  const newByCx = new Map(newVariants.map((v) => [v.cxId, v]));
  const cxIds = [...new Set([...oldByCx.keys(), ...newByCx.keys()])].sort(
    (a, b) => a.localeCompare(b),
  );

  for (const cxId of cxIds) {
    const oldVar = oldByCx.get(cxId);
    const newVar = newByCx.get(cxId);
    // One resource composing all three parts. `cxId` is TRANSCOM taxonomy and
    // passes through untranslated; the property name is itself a resource, so
    // the whole label is built in one place a translator controls.
    const fieldLabel = (property: string): string =>
      t('sgd-diff-variant-field', { cxId, property });

    if (oldVar && !newVar) {
      fields.push({
        field: fieldLabel(t('string-field-source-text')),
        previous: displayText(oldVar.sourceText),
        current: NO_VALUE,
      });
      continue;
    }
    if (!oldVar && newVar) {
      fields.push({
        field: fieldLabel(t('string-field-source-text')),
        previous: NO_VALUE,
        current: displayText(newVar.sourceText),
      });
      continue;
    }
    if (oldVar && newVar) {
      if (oldVar.sourceText !== newVar.sourceText) {
        fields.push({
          field: fieldLabel(t('string-field-source-text')),
          previous: displayText(oldVar.sourceText),
          current: displayText(newVar.sourceText),
        });
      }
      if ((oldVar.contextNote ?? '') !== (newVar.contextNote ?? '')) {
        fields.push({
          field: fieldLabel(t('string-field-context-note')),
          previous: displayText(oldVar.contextNote),
          current: displayText(newVar.contextNote),
        });
      }
    }
  }

  return fields;
}
